/**
 * Helpers to convert planned actions into parent by object actions (for the client and server)
 */

import _, {mapObject} from 'underscore';

import {PrimaryObjectPlan, SecondaryObjectPlan} from 'config-migration/types/MigrationObjects';
import {DestinationObject} from 'config-migration/types/MigrationObjects';
import {MigrationObjectPlanById} from 'config-migration/types/MigrationObjects';
import {
  ObjectAction,
  ObjectActionType,
  ServerObjectAction,
  ActionsByObjectId,
} from 'config-migration/types/objectActions';

import {MigrationStep} from '../../types/MigrationStep';
import {filterMigrationObjectsToAllPlannedSteps} from '../ObjectSectionConfig';
import {allChildMigrationPlans} from './ConfigMigrationPlan';

/**
 * Given the mapping of all current objects and their plans, the list of equivalent/kept objects, and the
 *  current migration step, returns an updated mapping of the planning actions available for each object
 *
 * @param migrationPlansById The map of currently planned objects and actions (keyed by provenance id)
 * @param keptObjects Objects that exist on both the destination and the source, unchanged
 *  (equivalent provenance-id matched objects)
 * @param currentMigrationStep The current migration step. All objects in steps up to this step will be
 *  transformed.
 */
export function transformPlannedActions(
  migrationPlansById: MigrationObjectPlanById,
  keptObjects: Array<DestinationObject>,
  currentMigrationStep?: MigrationStep
): ActionsByObjectId {
  let plannedObjects = Object.values(migrationPlansById);
  if (currentMigrationStep) {
    plannedObjects = filterMigrationObjectsToAllPlannedSteps(plannedObjects, currentMigrationStep);
  }

  return {
    ...transformPlannedRegularActions(plannedObjects),
    ...transformPlannedKeepActions(keptObjects, currentMigrationStep),
  };
}

function transformPlannedRegularActions(
  migrationPlans: Array<PrimaryObjectPlan | SecondaryObjectPlan>
): ActionsByObjectId {
  const plannedObjectsByObjectId = _.indexBy(migrationPlans, 'objectId');
  return _.mapObject(plannedObjectsByObjectId, (migrationObjectPlan) => {
    const childMigrationPlans = allChildMigrationPlans(migrationObjectPlan);
    if (childMigrationPlans.length > 0) {
      return {
        ...migrationObjectPlan.selectedAction,
        childActionsByObjectId: transformPlannedRegularActions(childMigrationPlans),
      };
    } else {
      return {
        ...migrationObjectPlan.selectedAction,
      };
    }
  });
}

/**
 * Given the current list of kept/equivalent objects, maps them from their provenance id to a `KeepAction`.
 *
 * @param keptObjects Objects that exist on both the destination and the source, unchanged
 *  (equivalent provenance-id matched objects)
 * @param currentMigrationStep The current migration step. All objects in steps up to this step will be
 *  transformed.
 *
 * Exported for testing.
 */
export function transformPlannedKeepActions(
  keptObjects: Array<DestinationObject>,
  currentMigrationStep?: MigrationStep
): ActionsByObjectId {
  const filteredKeptObjects = currentMigrationStep
    ? filterMigrationObjectsToAllPlannedSteps(keptObjects, currentMigrationStep)
    : keptObjects;

  const actionsByProvenanceUri: ActionsByObjectId = {};

  filteredKeptObjects.forEach((keptObject) => {
    actionsByProvenanceUri[keptObject.identifier.provenanceUri] = {
      type: ObjectActionType.KEEP,
      target: keptObject.identifier,
    };
  });
  return actionsByProvenanceUri;
}

export function transformObjectActionToServerRepresentation(objectAction: ObjectAction): ServerObjectAction {
  return {
    type: objectAction.type,
    target:
      objectAction.type === ObjectActionType.UPDATE || objectAction.type === ObjectActionType.KEEP
        ? {id: objectAction.target.apiIdentifier, archived: objectAction.target.archived}
        : null,
    childActionsByObjectId:
      objectAction.type === ObjectActionType.UPDATE || objectAction.type === ObjectActionType.KEEP
        ? mapObject(objectAction.childActionsByObjectId || {}, transformObjectActionToServerRepresentation)
        : null,
  };
}
